// @flow
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
// Components
import { Helmet } from 'react-helmet';
import { ColorRow, PageLoader } from '../../common';
import { UnauthenticatedRedirect } from '../../user';
import BlendTemplateListItem from '../components/BlendTemplateListItem';
// Analytics
import * as Analytics from '../../common/analytics';
// Actions
import * as actions from '../actions';
// Selectors
import currentOrderNumberConnector from '../../current-order/connectors/currentOrderNumberConnector';
import * as selectors from '../selectors';
// Images
import BlendBanner from '../images/blend_banner.png';
//Style
import './BlendsTemplateIndexPage.css';
// Types
import ArrowRight from '../../common/images/arrowRight.svg';
import Presentation from '../images/presentation.svg';
import type {
  Blend,
  BlendTemplate,
  BlendTemplateItem as BlendTemplateItemType,
} from '../types';

type Props = {
  blendTemplates: [BlendTemplate],
  loading: boolean,
  blendTemplateItemsByTemplateId: (
    templateId: number,
  ) => BlendTemplateItemType[],
  blendByTemplateIdAndOrderNumber: (
    orderNumber: string,
    templateId: number,
  ) => Blend,
  getBlendTemplates: () => void,
  getBlends: (orderNumber: string) => void,
  // From Connector
  currentOrderNumber: string,
};

/**
 * This page will show all the blend templates that belong to the current user.
 * Blend template that are complete will be able to be added to cart and have their quantity changed.
 * Blend templates that are NOT complete will have the option of navigating to the blend creation page to complete them.
 */

const LinkToNewBlendPage = (props: { text: String }) => {
  return (
    <Link
      className="blends-page-caculator-button"
      to="/blend/calculadora/new/"
      onClick={() => {
        Analytics.logTappedCreateBlend();
      }}
    >
      <span>{props.text}</span>
      <img src={ArrowRight} alt="Arrow Right" width="20px" height="20px" />
    </Link>
  );
};

const BlendsTemplateIndexPage = ({
  blendByTemplateIdAndOrderNumber,
  blendTemplateItemsByTemplateId,
  blendTemplates,
  loading,
  currentOrderNumber,
  getBlendTemplates,
  getBlends,
}: Props) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [templatesPerPage, setTemplatesPerPage] = useState(5);
  const [orderNumber, setOrderNumber] = useState(currentOrderNumber);

  const searchBlends = () => {
    getBlendTemplates();

    // If we already have an order number we should get all the blends. This is needed to correctly populate the addToCartButtons
    if (currentOrderNumber) {
      getBlends(currentOrderNumber);
    }
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // Sort blends by completed at such that newest are on top and incomplete at the bottom
  const sortedBlendTemplates = _.sortBy(
    blendTemplates,
    ['completedAt', 'updatedAt'],
    ['asc', 'asc'],
  );

  const indexOfLast = currentPage * templatesPerPage;

  const indexOfFirst = indexOfLast - templatesPerPage;

  const currentTemplates = sortedBlendTemplates.slice(
    indexOfFirst,
    indexOfLast,
  );

  const totalPages = Math.ceil(sortedBlendTemplates.length / templatesPerPage);

  useEffect(() => {
    searchBlends();
  }, []);

  useEffect(() => {
    // If our orderNumber changes we need to get the blends associated with the new number
    if (currentOrderNumber !== orderNumber) {
      setOrderNumber(currentOrderNumber);
      getBlends(currentOrderNumber);
    }

    const totalPages = Math.ceil(blendTemplates.length / templatesPerPage);

    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentOrderNumber, blendTemplates, templatesPerPage, currentPage]);

  return (
    <UnauthenticatedRedirect>
      <div className="blends-page-main">
        <Helmet>
          <title>Meus Blends - Bigdelivery, Seu açougue virtual</title>
        </Helmet>

        <ColorRow color="orange" text="MEUS BLENDS" />
      </div>
      <div>
        <img className="blends-page-banner-image" src={BlendBanner} alt="" />
      </div>
      {loading ? (
        <PageLoader />
      ) : (
        <>
          {sortedBlendTemplates.length > 0 ? (
            <div className="blends-page-container">
              <LinkToNewBlendPage text="Criar novo blend" />
              {currentTemplates.map(template => (
                <BlendTemplateListItem
                  key={template.id}
                  blendTemplate={template}
                  blendTemplateItems={blendTemplateItemsByTemplateId(
                    template.id,
                  )}
                  blend={blendByTemplateIdAndOrderNumber(
                    currentOrderNumber,
                    template.id,
                  )}
                  clickable={true}
                />
              ))}

              {sortedBlendTemplates.length > templatesPerPage && (
                <div className="pagination">
                  <button
                    className="pagination-button pagination-button-arrow"
                    onClick={() =>
                      handlePageChange(Math.max(currentPage - 1, 1))
                    }
                    disabled={currentPage === 1}
                  >
                    ‹
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => handlePageChange(i + 1)}
                      className={`pagination-button${currentPage === i + 1 ? ' active' : ''}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className="pagination-button pagination-button-arrow"
                    onClick={() =>
                      handlePageChange(Math.min(currentPage + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="blends-page-empty-state-container">
              <img src={Presentation} alt="Empty state" />
              <div>Ops! Parece que você ainda não adicionou nenhum blend.</div>
              <LinkToNewBlendPage text="Monte seu primeiro blend" />
            </div>
          )}
        </>
      )}
    </UnauthenticatedRedirect>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    blendTemplates: selectors.blendTemplatesSelector(state),
    blendTemplateItemsByTemplateId: (id: number | string) =>
      selectors.getBlendTemplateItemsByBlendTemplateIdSelector(state, id),
    blendByTemplateIdAndOrderNumber: (
      orderNumber: string,
      templateId: number,
    ) =>
      selectors.blendByOrderNumberAndTemplateIdSelector(
        state,
        orderNumber,
        templateId,
      ),
    loading: selectors.gettingBlendTemplatesLoadingSelector(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps) => {
  return {
    getBlendTemplates: () => {
      dispatch(actions.getBlendTemplates());
    },
    getBlends: (orderNumber: string) => {
      dispatch(actions.getBlends(orderNumber));
    },
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default currentOrderNumberConnector(
  withRouter(connector(BlendsTemplateIndexPage)),
);
